import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "api/axiosInstance";

import { useDispatch } from "react-redux";
import { loginSuccess } from "store/authSlice";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth } from "../../../firebase";
import { useAuth } from "../../../components/navigation/AuthenticationStateManager";

const AuthenticationForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoggingIn, setIsLoggingIn, login } = useAuth(); // Get global login state

  const [activeTab, setActiveTab] = useState("login");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ message: "", type: "" });

  // Debounce function to prevent rapid calls
  const debounceLogin = (callback, delay = 1000) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        callback(...args);
      }, delay);
    };
  };

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 4000);
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Get the ID token
      const idToken = await result.user.getIdToken();
      
      // Send token to backend for verification and user creation/login
      const res = await api.post("/api/auth/google", {
        idToken,
        email: result.user.email,
        name: result.user.displayName,
        avatar: result.user.photoURL
      });

      const { token, user } = res.data;
      dispatch(loginSuccess({ token, user }));
      showToast("Google login successful!", "success");

      setTimeout(() => {
        if (user.role === "ADMIN") {
          navigate("/admin");
          return;
        }

        // Redirect to homepage for all users
        navigate("/");
      }, 600);
    } catch (err) {
      showToast(err.response?.data?.message || "Google login failed", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateLogin = () => {
    const e = {};
    if (!validateEmail(loginForm.email)) e.email = "Valid email required";
    if (!loginForm.password) e.password = "Password required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateLogin()) return;
    if (isLoading || isLoggingIn) return; // prevent double click with global state
    setIsLoggingIn(true);
    
      
      try {
        setIsLoading(true);
        const res = await api.post("/api/auth/login", loginForm);
        const { token, user } = res.data;

        dispatch(loginSuccess({ token, user }));
        showToast("Login successful!", "success");
        console.log("Logged user:", user);

        // Use auth context login function to handle state and navigation
        console.log("Calling login function with:", { token, user });
        login(token, user);
      } catch (err) {
        showToast(err.response?.data?.message || "Login failed", "error");
      } finally {
        setIsLoading(false);
        setIsLoggingIn(false); // Reset login flag after completion
      }
  };

  return (
    <div className="w-full max-w-md mx-auto mt-12">
      <div className="bg-white p-6 rounded shadow-lg">

        {toast.message && (
          <div className={`mb-4 p-3 rounded text-sm ${
            toast.type === "success"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}>
            {toast.message}
          </div>
        )}

        <div className="flex border-b mb-6">
          <button
            className={`flex-1 pb-2 ${
              activeTab === "login"
                ? "border-b-2 border-blue-500 font-bold"
                : ""
            }`}
            onClick={() => setActiveTab("login")}
          >
            Sign In
          </button>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="email"
            id="email"
            name="email"
            placeholder="Email"
            className="w-full border p-2 rounded"
            value={loginForm.email}
            onChange={(e) =>
              setLoginForm({ ...loginForm, email: e.target.value })
            }
          />
          <input
            type="password"
            id="password"
            name="password"
            placeholder="Password"
            className="w-full border p-2 rounded"
            value={loginForm.password}
            onChange={(e) =>
              setLoginForm({ ...loginForm, password: e.target.value })
            }
          />
          <button
            disabled={isLoading || isLoggingIn}
            className="w-full bg-blue-500 text-white p-2 rounded"
          >
            {isLoading || isLoggingIn ? "Signing in..." : "Sign In"}
          </button>
        </form>
        
        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">OR</span>
          </div>
        </div>

        {/* Google Sign In Button */}
        <button
          onClick={handleGoogleSignIn}
          disabled={isLoading || isLoggingIn}
          className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white text-gray-700 p-3 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </button>
        
        {/* Register Navigation */}
        <div className="mt-6 text-center text-sm text-gray-600">
          Don't have an account?{" "}
          <Link 
            to="/register" 
            className="text-indigo-600 hover:text-indigo-800 hover:underline transition-colors"
          >
            Register
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationForm;
